import { TableSkeleton } from '@/components/ui/skeletons'
import { useIsMobile } from '@/hooks/use-mobile'
import type { ExpandedState, OnChangeFn } from '@tanstack/react-table'
import {
	createColumnHelper,
	getCoreRowModel,
	getExpandedRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SelectAllHeader, SelectRowCell } from './SelectColumnCells'
import { TableView } from './TableView'
import { mergeColumnOrder } from './columnOrder'
import type { CommonTableProps, SortingState, VisibilityState } from './types'
import { useCheckboxSelection } from './useCheckboxSelection'

export function CommonTable<TData>({
	data = [],
	columns,
	allData,
	totalCount,
	fetchAllSelectableIds,
	fetchSelectAllCount,
	resetSelectionOn,
	isLoading = false,
	error = null,
	loadingSkeleton,
	getRowId,
	enableSelection = false,
	selectionMode = 'multi',
	initialSelection = [],
	onSelectionChange,
	selectableRowFilter,
	disabledRowTooltip,
	disableSelectAll = false,
	selectAllDisabledTooltip,
	onSelectionStateChange,
	selectionToolbarProps,
	enableColumnReordering = false,
	frozenColumnIds,
	frozenRightColumnIds,
	staticColumnIds,
	initialColumnOrder,
	onColumnOrderChange,
	onRowClick,
	onRowDoubleClick,
	onRowRightClick,
	onRowKeySelect,
	onGroupRowKeySelect,
	onHoveredRowChange,
	highlightedRowId,
	getRowVariant,
	getRowClassName,
	enableGrouping = false,
	grouping = [],
	initialExpandedState,
	expanded,
	onExpandedChange,
	preserveSelectionToolbarInBottomBar = false,
	onScroll,
	dataTestId,
	manualSorting = false,
	sorting: controlledSorting,
	onSortingChange,
}: CommonTableProps<TData>) {
	const [sortingInternal, setSortingInternal] = useState<SortingState>([])

	// When manualSorting is on and a sorting prop is supplied, the sort state is
	// controlled by the caller (server-side sort); otherwise use internal client-side state.
	const isControlledSorting = manualSorting && controlledSorting !== undefined
	const sorting = isControlledSorting ? controlledSorting : sortingInternal
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
	// Frozen columns are pinned by `frozenColumnIds` and can't be dragged, so the order state and
	// everything persisted from it carry movable columns only. Their position never comes from storage.
	const frozenIds = useMemo(() => frozenColumnIds ?? [], [frozenColumnIds])
	const excludeFrozen = useCallback((ids: string[]) => ids.filter(id => !frozenIds.includes(id)), [frozenIds])
	// Read side of the same rule: a column that was movable when its order was saved can become
	// frozen later, since some pages vary `frozenColumnIds` by viewport or grouping. Dropping those
	// ids here keeps them out of the movable order, and out of what gets persisted back.
	const [columnOrder, setColumnOrder] = useState<string[]>(() => excludeFrozen(initialColumnOrder ?? []))

	// Display preferences hydrate after mount, so the saved order often arrives a render or two late.
	// Keyed by content, not identity: callers commonly pass a fresh array literal every render, and
	// re-adopting the same value would fight a drag the user just made.
	const savedOrderKey = excludeFrozen(initialColumnOrder ?? []).join('\u0000')
	const appliedOrderKeyRef = useRef<string | null>(null)
	useEffect(() => {
		if (savedOrderKey === appliedOrderKeyRef.current) {
			return
		}
		appliedOrderKeyRef.current = savedOrderKey
		setColumnOrder(savedOrderKey === '' ? [] : savedOrderKey.split('\u0000'))
	}, [savedOrderKey])
	// Use custom initial expanded state if provided, otherwise expand all groups
	const [expandedInternal, setExpandedInternal] = useState<ExpandedState>(initialExpandedState ?? true)

	const isControlledExpanded = expanded !== undefined
	const finalExpanded = isControlledExpanded ? expanded : expandedInternal
	const finalSetExpanded = isControlledExpanded ? onExpandedChange : setExpandedInternal

	// Track if we've initialized the expanded state to prevent re-expansion on data changes
	// This prevents groups from auto-expanding when user interacts with the table
	const hasInitializedExpanded = useRef(false)

	const isMobile = useIsMobile()

	// Track the previous initialExpandedState to detect meaningful changes
	const prevInitialExpandedStateRef = useRef<ExpandedState | undefined>(undefined)

	// Only expand all rows once when grouping is first enabled, not on every data change.
	// This prevents unwanted re-expansion when user clicks checkboxes or data updates.
	useEffect(() => {
		if (enableGrouping && !hasInitializedExpanded.current) {
			// Use custom initial expanded state if provided, otherwise expand all
			if (!isControlledExpanded) {
				setExpandedInternal(initialExpandedState ?? true)
			}
			hasInitializedExpanded.current = true
			prevInitialExpandedStateRef.current = initialExpandedState
		} else if (enableGrouping && hasInitializedExpanded.current) {
			// Check if initialExpandedState changed from `true` to a specific object
			// This handles the case where data loads after initial render
			const prevState = prevInitialExpandedStateRef.current
			if (prevState === true && typeof initialExpandedState === 'object' && initialExpandedState !== null) {
				if (!isControlledExpanded) {
					setExpandedInternal(initialExpandedState)
				}
				prevInitialExpandedStateRef.current = initialExpandedState
			}
		} else if (!enableGrouping) {
			// Reset the flag when grouping is disabled so it can initialize again when re-enabled
			hasInitializedExpanded.current = false
			prevInitialExpandedStateRef.current = undefined
		}
	}, [enableGrouping, initialExpandedState, isControlledExpanded])

	const rowIdFunction =
		getRowId ||
		((row: TData) => {
			return data.indexOf(row).toString()
		})

	const [autoPersistentIds, setAutoPersistentIds] = useState<string[]>([])

	const autoSelectionChange = useCallback(
		(
			selectedIds: string[],
			selectedData: TData[],
			action: 'select' | 'deselect' | 'selectAll' | 'deselectAll' | 'selectRange' | 'deselectRange',
		) => {
			setAutoPersistentIds(selectedIds)
			onSelectionChange?.(selectedIds, selectedData, action)
		},
		[onSelectionChange],
	)

	const effectiveInitialSelection = initialSelection.length ? initialSelection : autoPersistentIds
	const effectiveSelectionChange = onSelectionChange || autoSelectionChange

	const selectionState = useCheckboxSelection({
		data,
		getRowId: rowIdFunction,
		initialSelection: effectiveInitialSelection,
		onSelectionChange: effectiveSelectionChange,
		onSelectionStateChange,
		selectableRowFilter,
		allData,
		totalCount,
		fetchAllSelectableIds,
		fetchSelectAllCount,
		resetOn: resetSelectionOn,
	})

	const finalColumns = useMemo(() => {
		if (!enableSelection) {
			return columns
		}

		const columnHelper = createColumnHelper<TData>()

		const checkboxColumn = columnHelper.display({
			id: 'select',
			meta: { alignment: 'center' as const },
			header: SelectAllHeader,
			cell: SelectRowCell,
		})

		return [checkboxColumn, ...columns]
	}, [enableSelection, columns])

	// TanStack moves every id a saved order lists to the front, which would put a freshly enabled
	// column right after the first one — mergeColumnOrder instead keeps an unlisted id in its defined slot.
	const effectiveColumnOrder = useMemo(() => {
		const liveIds = excludeFrozen(
			finalColumns.map(col => col.id || (col as { accessorKey?: string }).accessorKey || 'unknown'),
		)
		return mergeColumnOrder(liveIds, frozenIds, columnOrder)
	}, [frozenIds, columnOrder, finalColumns, excludeFrozen])

	const handleColumnReorder = (activeId: string, overId: string) => {
		if (activeId === overId) {
			return
		}

		setColumnOrder(prevOrder => {
			// Drag against what is on screen — effectiveColumnOrder, not the partial saved order.
			const newOrder = excludeFrozen(effectiveColumnOrder)
			const activeIndex = newOrder.indexOf(activeId)
			const overIndex = newOrder.indexOf(overId)

			if (activeIndex === -1 || overIndex === -1) {
				return prevOrder
			}

			// Remove the active item and insert it at the new position
			const [movedItem] = newOrder.splice(activeIndex, 1)
			newOrder.splice(overIndex, 0, movedItem)

			onColumnOrderChange?.(newOrder)

			return newOrder
		})
	}

	// Row model order matters: core -> grouped -> sorted -> expanded
	const table = useReactTable({
		meta: {
			selectColumn: {
				selectionState,
				getRowId,
				selectionMode,
				isMobile,
				disabledRowTooltip,
				disableSelectAll,
				selectAllDisabledTooltip,
				dataTestId,
			},
		},
		data,
		columns: finalColumns,
		getCoreRowModel: getCoreRowModel(),
		...(enableGrouping && {
			getGroupedRowModel: getGroupedRowModel(),
		}),
		// With manual sorting the server returns rows already ordered; skip the
		// client-side sorted row model so the received order is preserved.
		...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
		...(enableGrouping && {
			getExpandedRowModel: getExpandedRowModel(),
		}),
		// Prevent TanStack Table from auto-resetting expanded state when data/columns change
		// This preserves user's manual expand/collapse actions during interactions
		autoResetExpanded: false,
		onSortingChange: updater => {
			const next = typeof updater === 'function' ? updater(sorting) : updater
			if (!isControlledSorting) {
				setSortingInternal(next)
			}
			onSortingChange?.(next)
		},
		onColumnVisibilityChange: setColumnVisibility,
		onExpandedChange: finalSetExpanded as OnChangeFn<ExpandedState>,
		getRowId,
		initialState: {
			...(enableGrouping && {
				expanded: true,
			}),
		},
		state: {
			sorting,
			columnVisibility,
			columnOrder: effectiveColumnOrder,
			...(enableGrouping && {
				grouping,
				expanded: finalExpanded,
			}),
		},
	})

	if (isLoading) {
		return (
			loadingSkeleton ?? <TableSkeleton cols={columns.length} showCheckbox={enableSelection} className='size-full' />
		)
	}

	if (error) {
		return (
			<div className='border border-destructive/20 bg-destructive/10 text-destructive rounded-lg flex flex-1 min-h-0 items-center justify-center min-w-0'>
				<div className='text-center'>
					<div className='font-medium mb-2'>Error loading data</div>
					<div className='text-body opacity-90'>{error.message}</div>
				</div>
			</div>
		)
	}

	if (data.length === 0) {
		return (
			<div className='border border-dashed rounded-lg flex flex-1 min-h-0 items-center justify-center min-w-0'>
				<div className='text-center'>
					<div className='text-muted-foreground mb-2'>No data available</div>
					<div className='text-body text-muted-foreground'>Start by adding some data to see it here</div>
				</div>
			</div>
		)
	}

	return (
		<div
			data-testid={dataTestId}
			className='size-full flex flex-1 flex-col min-h-0 min-w-0 border overflow-hidden bg-card'>
			<TableView
				table={table}
				getRowId={getRowId}
				enableSelection={enableSelection}
				selectionState={enableSelection ? selectionState : undefined}
				selectionToolbarProps={selectionToolbarProps}
				frozenColumnIds={frozenColumnIds}
				frozenRightColumnIds={frozenRightColumnIds}
				staticColumnIds={staticColumnIds}
				enableColumnReordering={enableColumnReordering}
				onColumnReorder={handleColumnReorder}
				onRowClick={onRowClick}
				onRowDoubleClick={onRowDoubleClick}
				onRowRightClick={onRowRightClick}
				onRowKeySelect={onRowKeySelect}
				onGroupRowKeySelect={onGroupRowKeySelect}
				onHoveredRowChange={onHoveredRowChange}
				highlightedRowId={highlightedRowId}
				getRowVariant={getRowVariant}
				getRowClassName={getRowClassName}
				enableGrouping={enableGrouping}
				preserveSelectionToolbarInBottomBar={preserveSelectionToolbarInBottomBar}
				onScroll={onScroll}
				dataTestId={dataTestId}
			/>
		</div>
	)
}
